import React, { useState, useEffect, useMemo } from 'react';
import { FaMobileAlt, FaShare } from 'react-icons/fa';
import './InstallAppPrompt.css';

const STORAGE_KEY = 'installPromptDismissedAt';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const SHOW_DELAY_MS = 2000;

/**
 * Platform detection via user-agent and navigator.platform
 * Returns: 'ios' | 'android' | 'desktop'
 */
const getPlatform = () => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';

  // iOS: iPhone, iPad, iPod (including iPadOS 13+ which reports MacIntel)
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';

  // Android
  if (/Android/i.test(ua)) return 'android';

  return 'desktop';
};

const wasDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    return Date.now() - at < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

export const showInstallPrompt = () => {
  triggerNativeInstall();
};

const triggerNativeInstall = async () => {
  let deferredPrompt = window.__installPrompt;
  if (!deferredPrompt) {
    // Handle small race where click happens just before beforeinstallprompt is captured.
    deferredPrompt = await new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(null), 1200);
      const onReady = () => {
        clearTimeout(timeout);
        window.removeEventListener('installPromptAvailable', onReady);
        resolve(window.__installPrompt || null);
      };
      window.addEventListener('installPromptAvailable', onReady, { once: true });
    });
  }
  if (!deferredPrompt) return false;

  try {
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    window.__installPrompt = null;
    return true;
  } catch (e) {
    console.warn('Install prompt error:', e);
    return false;
  }
};

const InstallAppPrompt = () => {
  const platform = useMemo(() => getPlatform(), []);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already installed (PWA standalone or iOS home screen)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    const show = () => setVisible(true);
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.__installPrompt = e;
    };
    const onAppInstalled = () => {
      window.__installPrompt = null;
      setVisible(false);
    };
    const timer = setTimeout(() => {
      if (!wasDismissedRecently()) setVisible(true);
    }, SHOW_DELAY_MS);
    window.addEventListener('showInstallPrompt', show);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('showInstallPrompt', show);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleNotNow = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {}
    setVisible(false);
  };

  const handleInstall = async () => {
    // Android/Desktop browsers: open native install dialog directly when available.
    if (await triggerNativeInstall()) {
      setVisible(false);
      return;
    }

    // If native install prompt is unavailable, do not show manual desktop/mobile fallback panel.
    setVisible(false);
  };

  if (!visible) return null;

  // iOS Safari: beforeinstallprompt not supported. Show custom "Add to Home Screen" instruction prompt.
  if (platform === 'ios') {
    return (
      <div className="install-prompt-wrap" role="dialog" aria-label="Install app - iOS">
        <div className="install-prompt-card install-prompt-card-instructions">
          <div className="install-prompt-icon">
            <FaShare />
          </div>
          <div className="install-prompt-text">
            <h3 className="install-prompt-title">Install UBSAAPP on iPhone/iPad</h3>
            <p className="install-prompt-desc">Tap Share → Add to Home Screen</p>
            <ol className="install-prompt-steps" aria-label="Installation steps">
              <li>Tap the <strong>Share</strong> button <FaShare className="install-prompt-inline-icon" /> at the bottom of Safari</li>
              <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
              <li>Tap <strong>Add</strong> in the top right</li>
            </ol>
            <p className="install-prompt-desc">The app icon will appear on your home screen. Open it anytime to book stays.</p>
          </div>
          <button type="button" className="install-prompt-btn install-prompt-btn-primary" onClick={handleNotNow}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="install-prompt-wrap" role="dialog" aria-label="Install app">
      <div className="install-prompt-card">
        <div className="install-prompt-icon">
          <FaMobileAlt />
        </div>
        <div className="install-prompt-text">
          <h3 className="install-prompt-title">Get the UBSAAPP app</h3>
          <p className="install-prompt-desc">
            Book stays on your phone. Download the app.
          </p>
        </div>
        <div className="install-prompt-actions">
          <button type="button" className="install-prompt-btn install-prompt-btn-primary" onClick={handleInstall}>
            Install
          </button>
          <button type="button" className="install-prompt-btn install-prompt-btn-secondary" onClick={handleNotNow}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
